import React, { useMemo, useState, useEffect } from "react";
import { useGlobalContext } from "../Config";

import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { useNavigate } from "react-router-dom";

const CargaBoletaPdf = () => {
  const { baseUrl } = useGlobalContext();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const [empresaId, setEmpresaId] = useState(null);
  const [proveedorId, setProveedorId] = useState(null);

  const [anio, setAnio] = useState(null);
  const [mes, setMes] = useState(null);

  const [files, setFiles] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [lastQuery, setLastQuery] = useState(null);
  const [mensaje, setMensaje] = useState(null);

  const [isLoadingRegistradas, setIsLoadingRegistradas] = useState(false);
  const [boletasRegistradas, setBoletasRegistradas] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editRowId, setEditRowId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfTitle, setPdfTitle] = useState("");

  const [proveedoresOptions, setProveedoresOptions] = useState([]);
  const [proveedoresRaw, setProveedoresRaw] = useState([]);

  const [editEmpresaId, setEditEmpresaId] = useState(null);
  const [editEmpresaRut, setEditEmpresaRut] = useState("");
  const [editMedidoresText, setEditMedidoresText] = useState("");

  const [periodoOptionsRegistradas, setPeriodoOptionsRegistradas] = useState([
    { label: "Todos", value: "" },
  ]);
  const [periodoSelRegistradas, setPeriodoSelRegistradas] = useState("");

  const CREATE_PROVEEDOR_VALUE = "__CREATE_PROVEEDOR__";

  const monedasOptions = [
    { label: "CLP", value: "CLP" },
    { label: "UF", value: "UF" },
    { label: "USD", value: "USD" },
  ];

  useEffect(() => {
    const cargarProveedores = async () => {
      try {
        const data = await fetchJson(`${baseUrl}proveedor/buscar`, {
          method: "GET",
        });
        const list = Array.isArray(data?.proveedores) ? data.proveedores : [];

        setProveedoresRaw(list);

        const options = list.map((p) => ({
          label: p?.razonSocial ?? `Proveedor ${p?.id}`,
          value: p?.id,
        }));

        options.push({
          label: "➕ Crear / administrar proveedores…",
          value: CREATE_PROVEEDOR_VALUE,
        });

        setProveedoresOptions(options);
      } catch (e) {
        console.error(e);
        setMensaje({
          severity: "error",
          text: "No se pudieron cargar los proveedores.",
        });
        setProveedoresRaw([]);
        setProveedoresOptions([]);
      }
    };

    cargarProveedores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aniosOptions = [2024, 2025, 2026].map((y) => ({
    label: String(y),
    value: y,
  }));

  const buildPeriodo = () => {
    if (!anio || !mes) return "";
    return `${anio}-${String(mes).padStart(2, "0")}`;
  };

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: "include",
      redirect: "follow",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.respuesta === false) {
      const msg = data?.mensaje || `Error HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  };

  // --------- helpers JSON path ----------
  const getByPath = (obj, path, def = "") => {
    if (!obj) return def;
    const parts = String(path).split(".");
    let cur = obj;
    for (const p of parts) {
      if (cur == null) return def;
      cur = cur[p];
    }
    return cur ?? def;
  };

  const setByPath = (obj, path, value) => {
    const parts = String(path).split(".");
    const out = { ...(obj || {}) };
    let cur = out;
    for (let i = 0; i < parts.length; i++) {
      const key = parts[i];
      if (i === parts.length - 1) {
        cur[key] = value;
        break;
      }
      const next = cur[key];
      cur[key] =
        next && typeof next === "object" && !Array.isArray(next)
          ? { ...next }
          : {};
      cur = cur[key];
    }
    return out;
  };

  // ---- HELPER fechas ----
  const parseIsoToDate = (iso) => {
    if (!iso) return null; // iso: YYYY-MM-DD
    const m = String(iso)
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const dt = new Date(y, mo - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };

  const toIsoFromDate = (dt) => {
    if (!dt || !(dt instanceof Date) || Number.isNaN(dt.getTime())) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const fmtDDMMYYYY = (iso) => {
    const dt = parseIsoToDate(iso);
    if (!dt) return "—";
    const d = String(dt.getDate()).padStart(2, "0");
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const y = dt.getFullYear();
    return `${d}-${m}-${y}`;
  };

  const getPeriodoYYYYMM = (iso) => {
    const m = String(iso || "")
      .trim()
      .match(/^(\d{4})-(\d{2})-\d{2}$/);
    return m ? `${m[1]}-${m[2]}` : "";
  };

  const fmtPeriodoMMYYYY = (yyyyMM) => {
    const m = String(yyyyMM || "").match(/^(\d{4})-(\d{2})$/);
    if (!m) return String(yyyyMM || "");
    return `${m[2]}-${m[1]}`; // MM-YYYY
  };

  const resetFiles = () => {
    const el = document.getElementById("pdf_input");
    if (el) el.value = "";
    setFiles([]);
  };

  const fmtRut = (rut) => {
    const clean = String(rut || "")
      .replace(/\./g, "")
      .replace(/-/g, "")
      .replace(/\s/g, "")
      .toUpperCase();

    if (!clean || clean.length < 2) return "—";

    const cuerpo = clean.slice(0, -1);
    const dv = clean.slice(-1);

    const cuerpoFmt = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return `${cuerpoFmt}-${dv}`;
  };

  // ✅ NUEVO: llave para comparar duplicados (empresa+proveedor+número boleta)
  const keyBoleta = (empresaId0, proveedorId0, numeroBoleta0) => {
    const emp = String(empresaId0 ?? "").trim();
    const prov = String(proveedorId0 ?? "").trim();
    const num = String(numeroBoleta0 ?? "").trim();
    return `${emp}||${prov}||${num}`;
  };

  const existentesSet = useMemo(() => {
    const s = new Set();
    for (const b of boletasRegistradas || []) {
      s.add(keyBoleta(b?.empresaId, b?.proveedorId, b?.numeroBoleta));
    }
    return s;
  }, [boletasRegistradas]);

  const isDuplicadaExtract = (row) => {
    if (!row) return false;

    if (row?.duplicada === true) return true;
    if (row?.duplicadaPorSha === true) return true;
    if (row?.duplicadaPorBoleta === true) return true;

    const num = getByPath(row?.data, "document.numero_boleta", "");
    if (!num) return false;

    return existentesSet.has(keyBoleta(row?.empresaId, proveedorId, num));
  };

  const duplicadasRegistradasSet = useMemo(() => {
    const s = new Set();

    for (const r of resultados || []) {
      const num = getByPath(r?.data, "document.numero_boleta", "");
      const emp = r?.empresaId;
      if (!num || !emp) continue;

      if (isDuplicadaExtract(r)) {
        s.add(keyBoleta(emp, proveedorId, num));
      }
    }

    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultados, proveedorId, existentesSet]);

  const rowClassNameResultados = (rowData) => {
    return isDuplicadaExtract(rowData) ? "row-duplicada" : "";
  };

  const rowClassNameRegistradas = (rowData) => {
    const k = keyBoleta(
      rowData?.empresaId,
      rowData?.proveedorId,
      rowData?.numeroBoleta,
    );
    return duplicadasRegistradasSet.has(k) ? "row-duplicada" : "";
  };

  const duplicadoDetalleBody = (row) => {
    const d = row?.duplicadoDetalle;
    if (!d) return <span style={{ color: "#6c757d" }}>—</span>;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <small>
          <b>ID:</b> {d?.id ?? "—"}
        </small>
        <small>
          <b>N°:</b> {d?.numeroBoleta ?? "—"}
        </small>
        <small>
          <b>Fecha pago:</b> {d?.fechaPago ?? "—"}
        </small>
        <small>
          <b>Periodo hasta:</b> {d?.periodoHasta ?? "—"}
        </small>
        <small>
          <b>PDF ID:</b> {d?.pdfUploadId ?? "—"}
        </small>
      </div>
    );
  };

  const duplicadasCount = useMemo(() => {
    return (resultados || []).filter((r) => isDuplicadaExtract(r)).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultados, existentesSet, proveedorId]);

  // --------- base64 ----------
  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    const onlyPdf = list.filter((f) => {
      const name = (f?.name || "").toLowerCase();
      return name.endsWith(".pdf") || f?.type === "application/pdf";
    });

    if (!onlyPdf.length) {
      setMensaje({
        severity: "warn",
        text: "Seleccione uno o más archivos PDF.",
      });
      resetFiles();
      return;
    }

    setFiles(onlyPdf);
    setMensaje(null);
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () =>
        reject(reader.error || new Error("Error leyendo archivo"));
      reader.readAsDataURL(file);
    });

  // --------- cargar boletas registradas ----------
  const cargarBoletasRegistradas = async () => {
    if (!proveedorId) {
      setBoletasRegistradas([]);
      setPeriodoSelRegistradas("");
      setPeriodoOptionsRegistradas([{ label: "Todos", value: "" }]);
      return;
    }

    setIsLoadingRegistradas(true);
    try {
      const params = new URLSearchParams();
      params.set("proveedorId", String(proveedorId));
      params.set("limit", "200");

      // opcional: si ya tienes empresaId y quieres acotar (NO es filtro periodo)
      if (empresaId) params.set("empresaId", String(empresaId));

      const url = `${baseUrl}boletaPdf/list?${params.toString()}`;
      const data = await fetchJson(url, { method: "GET" });

      const items = Array.isArray(data?.items) ? data.items : [];
      setBoletasRegistradas(items);

      // ✅ periodos desde data obtenida
      const setP = new Set();
      for (const r of items) {
        const p = getPeriodoYYYYMM(r?.periodoHasta);
        if (p) setP.add(p);
      }
      const sorted = Array.from(setP).sort((a, b) => b.localeCompare(a));

      const options = [
        { label: "Todos", value: "" },
        ...sorted.map((p) => ({ label: fmtPeriodoMMYYYY(p), value: p })),
      ];
      setPeriodoOptionsRegistradas(options);

      // si el periodo seleccionado ya no existe, reset
      if (periodoSelRegistradas && !setP.has(periodoSelRegistradas)) {
        setPeriodoSelRegistradas("");
      }
    } catch (e) {
      console.error(e);
      setBoletasRegistradas([]);
      setPeriodoOptionsRegistradas([{ label: "Todos", value: "" }]);
    } finally {
      setIsLoadingRegistradas(false);
    }
  };

  useEffect(() => {
    setPeriodoSelRegistradas("");
    cargarBoletasRegistradas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedorId, empresaId]);

  const boletasRegistradasFiltradas = useMemo(() => {
    if (!periodoSelRegistradas) return boletasRegistradas;

    return (boletasRegistradas || []).filter(
      (r) => getPeriodoYYYYMM(r?.periodoHasta) === periodoSelRegistradas,
    );
  }, [boletasRegistradas, periodoSelRegistradas]);

  // --------- UI validate ----------
  const validarLectura = () => {
    if (!proveedorId) {
      setMensaje({ severity: "warn", text: "Debe seleccionar proveedor." });
      return false;
    }
    if (!files?.length) {
      setMensaje({
        severity: "warn",
        text: "Debe seleccionar al menos un PDF.",
      });
      return false;
    }
    return true;
  };

  const validarConfirmacion = () => {
    if (!resultados?.length) {
      setMensaje({
        severity: "warn",
        text: "No hay resultados para confirmar.",
      });
      return false;
    }

    const itemsOk = resultados.filter((r) => r?.status === "OK");
    if (itemsOk.length === 0) {
      setMensaje({
        severity: "warn",
        text: "No hay boletas válidas para guardar.",
      });
      return false;
    }

    const faltantes = itemsOk.filter(
      (r) =>
        !String(getByPath(r?.data, "document.fecha_pago", "") || "").trim(),
    );

    if (faltantes.length > 0) {
      setMensaje({
        severity: "warn",
        text: `Hay ${faltantes.length} boleta(s) sin fecha_pago. Edítalas antes de confirmar.`,
      });
      return false;
    }

    const empIds = Array.from(
      new Set(itemsOk.map((r) => r?.empresaId).filter((v) => v != null)),
    );
    const empRuts = Array.from(
      new Set(itemsOk.map((r) => r?.empresaRut).filter(Boolean)),
    );

    if (empIds.length > 1) {
      setMensaje({
        severity: "warn",
        text: `Las boletas pertenecen a múltiples empresas (${empRuts.join(
          ", ",
        )}). Sepáralas por empresa antes de confirmar.`,
      });
      return false;
    }

    const duplicadas = resultados.filter((r) => isDuplicadaExtract(r));

    if (duplicadas.length > 0) {
      setMensaje({
        severity: "warn",
        text: `Hay ${duplicadas.length} boleta(s) duplicada(s). No se pueden confirmar.`,
      });
      return false;
    }

    return true;
  };

  // --------- actions ----------
  const leerPdfs = async () => {
    setMensaje(null);
    if (!validarLectura()) return;

    setResultados([]);
    setLastQuery(null);

    setIsLoading(true);

    try {
      const periodo = buildPeriodo();

      const archivos = [];
      for (const f of files) {
        const b64 = await fileToBase64(f);
        archivos.push({ nombre: f.name, base64: b64 });
      }

      const payload = { proveedorId, archivos };

      const data = await fetchJson(`${baseUrl}boletaPdf/extract`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const list = Array.isArray(data?.resultados) ? data.resultados : [];

      const normalized = list.map((r, idx) => ({
        ...r,
        _rowKey: String(r?.pdfUploadId ?? r?.nombre ?? idx),
        data: r?.data && typeof r.data === "object" ? r.data : {},
      }));

      const empIds = Array.from(
        new Set(normalized.map((r) => r?.empresaId).filter((v) => v != null)),
      );

      if (empIds.length === 1) {
        setEmpresaId(empIds[0]);
      } else if (empIds.length > 1) {
        setMensaje({
          severity: "warn",
          text: `Las boletas vienen con múltiples empresas: ${empIds.join(
            ", ",
          )}. No se puede confirmar en un solo envío.`,
        });
        // Igual puedes mostrar resultados, pero bloquear confirm.
      } else {
        setMensaje({
          severity: "warn",
          text: "No se pudo resolver empresa desde la boleta.",
        });
      }

      const empresaIdDetectada = empIds.length === 1 ? empIds[0] : null;

      if (empresaIdDetectada) setEmpresaId(empresaIdDetectada);

      setLastQuery({
        empresaId: empresaIdDetectada,
        proveedorId,
        periodo,
        count: normalized.length,
      });
      setResultados(normalized);

      // refrescar registradas
      await cargarBoletasRegistradas();

      const duplicadas = normalized.filter(
        (r) => r?.duplicada === true || isDuplicadaExtract(r),
      );
      const validas = normalized.filter(
        (r) => !(r?.duplicada === true || isDuplicadaExtract(r)),
      );

      setMensaje({
        severity: duplicadas.length > 0 ? "warn" : "success",
        text:
          `Lectura finalizada. ${validas.length} documento(s) listo(s) para guardar.` +
          (duplicadas.length > 0
            ? ` ${duplicadas.length} documento(s) fueron detectadas como duplicados y no podrán confirmarse.`
            : ""),
      });

      // ✅ limpiar selector de archivos tras leer
      resetFiles();
    } catch (e) {
      console.error(e);
      setMensaje({
        severity: "error",
        text: "Error leyendo PDFs: " + (e?.message || e),
      });
      setResultados([]);
      setLastQuery(null);
      resetFiles();
    } finally {
      setIsLoading(false);
    }
  };

  const medidoresBody = (row) => {
    const items = getByPath(row?.data, "details.items_consumo", []);
    const codigos = Array.isArray(items)
      ? Array.from(
          new Set(
            items.map((x) => String(x?.medidor ?? "").trim()).filter(Boolean),
          ),
        )
      : [];
    return codigos.length ? codigos.join(", ") : "—";
  };

  const confirmar = async () => {
    setMensaje(null);
    if (!validarConfirmacion()) return;

    const periodo = buildPeriodo();

    const payload = {
      empresaId,
      proveedorId,
      items: resultados
        .filter((r) => r?.status === "OK" && !isDuplicadaExtract(r))
        .map((r) => ({
          pdfUploadId: r?.pdfUploadId ?? null,
          nombre: r?.nombre ?? "",
          data: r?.data ?? {},
          duplicada: r?.duplicada === true,
        })),
    };

    const doConfirm = async () => {
      setIsLoading(true);
      try {
        const resp = await fetchJson(`${baseUrl}boletaPdf/confirm`, {
          method: "POST",
          body: JSON.stringify(payload),
        });

        const guardados = Array.isArray(resp?.guardados) ? resp.guardados : [];
        const errores = Array.isArray(resp?.errores) ? resp.errores : [];

        if (guardados.length > 0 && errores.length === 0) {
          setMensaje({
            severity: "success",
            text: `Se guardaron ${guardados.length} boleta(s) correctamente.`,
          });
        } else if (guardados.length > 0 && errores.length > 0) {
          setMensaje({
            severity: "warn",
            text: `Se guardaron ${guardados.length} boleta(s), pero hubo ${errores.length} error(es).`,
          });
        } else {
          setMensaje({
            severity: "error",
            text: errores?.[0]?.error || "No se pudo guardar ninguna boleta.",
          });
        }

        await cargarBoletasRegistradas();

        resetFiles();
        setResultados([]);
        setLastQuery(null);

        const created = Array.isArray(resp?.medidoresCreados)
          ? resp.medidoresCreados
          : [];

        if (created.length > 0) {
          const ids = created
            .map((x) => Number(x?.medidorId))
            .filter((n) => Number.isFinite(n));

          sessionStorage.setItem(
            "inventario_new_medidores",
            JSON.stringify({ ids, empresaId, proveedorId }),
          );

          confirmDialog({
            header: "Medidores nuevos creados",
            icon: "pi pi-info-circle",
            message:
              `Se crearon ${created.length} medidor(es) nuevos al guardar la boleta.\n\n` +
              `¿Quieres ir a Inventario para completar sus datos?`,
            acceptLabel: "Ir a Inventario",
            rejectLabel: "Más tarde",
            accept: () => {
              navigate("/inventario", {
                state: { newMedidorIds: ids, empresaId, proveedorId },
              });
            },
          });
        }
      } catch (e) {
        console.error(e);
        setMensaje({
          severity: "error",
          text: "Error confirmando: " + (e?.message || e),
        });
      } finally {
        setIsLoading(false);
      }
    };

    confirmDialog({
      message: `¿Confirmar y guardar ${payload.items.length} boleta(s) del periodo ${periodo}?`,
      header: "Confirmar",
      icon: "pi pi-check",
      acceptLabel: "Sí",
      rejectLabel: "No",
      accept: doConfirm,
    });
  };

  const limpiar = () => {
    setEmpresaId(null);
    setProveedorId(null);
    setAnio(null);
    setMes(null);

    resetFiles();

    setResultados([]);
    setLastQuery(null);
    setMensaje(null);
    setBoletasRegistradas([]);

    setEditOpen(false);
    setEditRowId(null);
    setEditData(null);
    setJsonOpen(false);
    setJsonText("");

    setPdfOpen(false);
    setPdfUrl("");
    setPdfTitle("");
  };

  // --------- table helpers ----------
  const fmtMoneyCLP = (v) => {
    const n = Number(v ?? 0);
    if (!Number.isFinite(n)) return "-";
    return n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  };

  const statusBody = (row) => {
    const s = String(row?.status || "");
    const sev =
      s === "OK"
        ? "success"
        : s === "ERROR"
        ? "danger"
        : s
        ? "warning"
        : "info";
    return <Tag severity={sev} value={s || "—"} />;
  };

  const warningsBody = (row) => {
    const w = row?.warnings;
    const n = Array.isArray(w) ? w.length : 0;
    if (!n) return <span style={{ color: "#6c757d" }}>—</span>;
    return <Tag severity="warning" value={`${n} warning(s)`} />;
  };

  const boletaBody = (row) =>
    getByPath(row?.data, "document.numero_boleta", "—");
  const fechaPagoBody = (row) =>
    fmtDDMMYYYY(getByPath(row?.data, "document.fecha_pago", ""));
  const clienteBody = (row) => getByPath(row?.data, "header.cliente", "—");
  const contratoBody = (row) => getByPath(row?.data, "header.contrato", "—");
  const rutaBody = (row) => getByPath(row?.data, "header.ruta", "—");
  const periodoDesdeBody = (row) =>
    fmtDDMMYYYY(getByPath(row?.data, "header.periodo.desde", ""));
  const periodoHastaBody = (row) =>
    fmtDDMMYYYY(getByPath(row?.data, "header.periodo.hasta", ""));

  const montoBody = (row) => {
    const val = getByPath(row?.data, "header.monto_total.valor", null);
    if (val === null || val === undefined || val === "") return "—";
    return `$ ${fmtMoneyCLP(val)}`;
  };

  const duplicadoBody = (row) => {
    const dup = row?.duplicada === true || isDuplicadaExtract(row);
    if (!dup) return <Tag severity="success" value="OK" />;

    const motivo = row?.duplicadaPorBoleta
      ? "BOLETA YA REGISTRADA"
      : row?.duplicadaPorSha
      ? "PDF YA CARGADO"
      : "DUPLICADA";

    return <Tag severity="danger" value={motivo} />;
  };

  // --------- ver PDF ----------
  const openPdf = (row) => {
    const pdfUploadId = row?.pdfUploadId;
    if (!pdfUploadId) return;
    const url = `${baseUrl}boletaPdf/upload/${encodeURIComponent(pdfUploadId)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openPdfUploadId = (pdfUploadId) => {
    if (!pdfUploadId) return;
    const url = `${baseUrl}boletaPdf/upload/${encodeURIComponent(pdfUploadId)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const actionBody = (row) => (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <Button
        label="Editar"
        icon="pi pi-pencil"
        className="p-button-sm"
        onClick={() => openEdit(row)}
        disabled={isLoading || isDuplicadaExtract(row)}
      />
      <Button
        label="Ver PDF"
        icon="pi pi-file-pdf"
        className="p-button-sm p-button-text"
        onClick={() => openPdf(row)}
        disabled={isLoading}
      />
    </div>
  );

  // --------- edit modal ----------
  const openEdit = (row) => {
    const key = String(row?._rowKey ?? row?.pdfUploadId ?? row?.nombre ?? "");
    setEditRowId(key);

    const baseData =
      row?.data && typeof row.data === "object" ? { ...row.data } : {};
    setEditData(baseData);

    // empresa meta
    setEditEmpresaId(row?.empresaId ?? null);
    setEditEmpresaRut(row?.empresaRut ?? "");

    // medidores
    const items = getByPath(row?.data, "details.items_consumo", []);
    const codigos = Array.isArray(items)
      ? Array.from(
          new Set(
            items.map((x) => String(x?.medidor ?? "").trim()).filter(Boolean),
          ),
        )
      : [];
    setEditMedidoresText(codigos.join(", "));

    setEditOpen(true);
  };

  const saveEdit = () => {
    if (!editRowId) return;

    const nuevosMedidores = String(editMedidoresText || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const prevItems = getByPath(editData, "details.items_consumo", []);
    let nextData = editData ?? {};

    if (nuevosMedidores.length) {
      let nextItems;

      if (Array.isArray(prevItems) && prevItems.length) {
        nextItems = prevItems.map((it, i) => ({
          ...it,
          medidor: nuevosMedidores[i] ?? it?.medidor ?? "",
        }));
      } else {
        nextItems = nuevosMedidores.map((m) => ({ medidor: m }));
      }

      nextData = setByPath(nextData, "details.items_consumo", nextItems);
    }

    setResultados((prev) =>
      prev.map((r) => {
        const key = String(r?._rowKey ?? r?.pdfUploadId ?? r?.nombre ?? "");
        if (key !== editRowId) return r;

        return {
          ...r,
          empresaId: editEmpresaId ?? r?.empresaId ?? null, // se mantiene para confirm
          empresaRut: editEmpresaRut ?? r?.empresaRut ?? "", // se muestra en UI
          data: nextData ?? {},
        };
      }),
    );

    setEditOpen(false);
    setEditRowId(null);
    setEditData(null);
    setEditEmpresaId(null);
    setEditEmpresaRut("");
    setEditMedidoresText("");
  };

  const updateField = (path, value) => {
    setEditData((prev) => setByPath(prev || {}, path, value));
  };

  const tablaProps = {
    responsiveLayout: "scroll",
    className: "p-datatable-sm",
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [5, 10, 20],
    stripedRows: true,
    showGridlines: true,
  };

  const headerCard = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0, color: "#495057" }}>
            Carga y Lectura de Documenots PDF
          </h4>
          <small style={{ color: "#6c757d" }}>
            Suba boletas o facturas en PDF, revise los datos extraídos y
            confirme/edite antes de guardar.
          </small>
        </div>
      </div>
    ),
    [],
  );

  const periodoLabel = useMemo(() => {
    const p = buildPeriodo();
    return p ? p : "—";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anio, mes]);

  useEffect(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    if (y >= 2024 && y <= 2026) {
      setAnio(y);
      setMes(m);
    }
  }, []);

  useEffect(() => {
    if (!proveedorId) {
      setBoletasRegistradas([]);
      setPeriodoOptionsRegistradas([]);
      setPeriodoSelRegistradas("");
      return;
    }

    setPeriodoSelRegistradas("");
    cargarBoletasRegistradas({ proveedorId, empresaId, periodo: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedorId, empresaId]);

  useEffect(() => {
    if (!proveedorId) return;
    cargarBoletasRegistradas({
      proveedorId,
      empresaId,
      periodo: periodoSelRegistradas,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodoSelRegistradas]);

  const showResultados = !!lastQuery && resultados?.length > 0;

  return (
    <div className="col-12 md:col-12">
      <ConfirmDialog />

      {/* header */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
          marginBottom: "20px",
        }}
      >
        {headerCard}
      </div>

      {/* filtros */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <div className="grid">
          <div className="col-12 md:col-4">
            <label style={{ fontWeight: "bold" }}>Proveedor</label>
            <Dropdown
              value={proveedorId}
              options={proveedoresOptions}
              onChange={(e) => {
                const v = e.value;

                if (v === CREATE_PROVEEDOR_VALUE) {
                  setProveedorId(null);
                  setBoletasRegistradas([]);
                  setPeriodoSelRegistradas("");
                  navigate("/proveedores");
                  return;
                }

                setProveedorId(v);
              }}
              placeholder="Seleccione proveedor"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
            />
          </div>

          <div className="col-12">
            <label style={{ fontWeight: "bold" }}>PDF(s)</label>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <input
                id="pdf_input"
                type="file"
                accept="application/pdf,.pdf"
                multiple
                disabled={isLoading}
                onChange={onPickFiles}
                style={{
                  padding: "10px",
                  border: "1px solid #ced4da",
                  borderRadius: 6,
                  background: "#fff",
                }}
              />
              <div style={{ color: "#6c757d" }}>
                {files?.length
                  ? `${files.length} archivo(s) seleccionado(s)`
                  : "Sin archivos"}
              </div>
            </div>
          </div>

          <div className="col-12">
            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 10,
                flexWrap: "wrap",
              }}
            >
              <Button
                label="Leer PDFs"
                icon="pi pi-upload"
                onClick={leerPdfs}
                disabled={isLoading}
              />
              <Button
                label="Limpiar"
                icon="pi pi-times"
                className="p-button-text"
                onClick={limpiar}
                disabled={isLoading}
              />
            </div>
          </div>

          {mensaje && (
            <div className="col-12" style={{ marginTop: 10 }}>
              <Message severity={mensaje.severity} text={mensaje.text} />
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <ProgressSpinner />
        </div>
      )}

      {showResultados && (
        <>
          {/* resumen */}
          <div className="grid" style={{ marginBottom: 16 }}>
            <div className="col-12 md:col-12">
              <Card
                style={{
                  borderRadius: 10,
                  boxShadow: "0px 0px 10px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div style={{ color: "#6c757d", fontWeight: 700 }}>
                      Consulta
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <strong>Empresa:</strong> {empresaId ?? "—"}{" "}
                      <span style={{ color: "#6c757d" }}> | </span>
                      <strong>Proveedor:</strong> {proveedorId ?? "—"}{" "}
                      <span style={{ color: "#6c757d" }}> | </span>
                      <strong>Periodo:</strong> {periodoLabel}
                      {duplicadasCount > 0 && (
                        <>
                          {" "}
                          <span style={{ color: "#6c757d" }}> | </span>
                          <Tag
                            severity="danger"
                            value={`${duplicadasCount} duplicada(s)`}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#6c757d", fontWeight: 700 }}>
                      Archivos
                    </div>
                    <div
                      style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}
                    >
                      {resultados?.length ?? 0}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <div
            style={{
              padding: "20px",
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
              marginBottom: "20px",
            }}
          >
            <div style={{ marginBottom: 10 }}>
              <strong>Resultado:</strong>{" "}
              {lastQuery
                ? `Empresa ${lastQuery.empresaId} - Proveedor ${lastQuery.proveedorId} - Periodo ${lastQuery.periodo}`
                : "Sin lectura"}
              {"  "}
              <span style={{ color: "#6c757d" }}>
                ({resultados.length} archivo(s))
              </span>
            </div>

            <DataTable
              value={resultados}
              dataKey={(r) => String(r?._rowKey ?? "")}
              rowClassName={rowClassNameResultados}
              {...tablaProps}
            >
              <Column
                header="Acciones"
                body={actionBody}
                style={{ minWidth: 320 }}
              />
              <Column
                header="N° Documento"
                body={boletaBody}
                style={{ minWidth: 160 }}
              />
              <Column
                header="Empresa"
                body={(row) => fmtRut(row?.empresaRut)}
                style={{ minWidth: 140 }}
              />
              <Column
                header="Medidores"
                body={medidoresBody}
                style={{ minWidth: 260 }}
              />
              <Column
                field="nombre"
                header="Archivo"
                style={{ minWidth: 260 }}
              />
              <Column
                header="Status"
                body={statusBody}
                style={{ minWidth: 120 }}
              />
              <Column
                header="Warnings"
                body={warningsBody}
                style={{ minWidth: 160 }}
              />
              <Column
                header="Duplicado"
                body={duplicadoBody}
                style={{ minWidth: 140 }}
              />
              <Column
                header="Registro existente"
                body={duplicadoDetalleBody}
                style={{ minWidth: 220 }}
              />
              <Column
                header="Fecha pago"
                body={fechaPagoBody}
                style={{ minWidth: 160 }}
              />
              <Column
                header="Cliente"
                body={clienteBody}
                style={{ minWidth: 220 }}
              />
              <Column
                header="Contrato"
                body={contratoBody}
                style={{ minWidth: 140 }}
              />
              <Column header="Ruta" body={rutaBody} style={{ minWidth: 120 }} />
              <Column
                header="Periodo desde"
                body={periodoDesdeBody}
                style={{ minWidth: 160 }}
              />
              <Column
                header="Periodo hasta"
                body={periodoHastaBody}
                style={{ minWidth: 160 }}
              />
              <Column
                header="Monto total"
                body={montoBody}
                style={{ minWidth: 160 }}
              />
            </DataTable>

            {resultados.length > 0 && duplicadasCount === 0 && (
              <div
                style={{
                  marginTop: 16,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  label="Confirmar y Guardar"
                  icon="pi pi-check"
                  className="p-button-success"
                  onClick={confirmar}
                  disabled={isLoading || !resultados?.length}
                />
              </div>
            )}
          </div>
        </>
      )}

      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div>
            <strong>Documentos ya registrados</strong>{" "}
            <span style={{ color: "#6c757d" }}>
              (Proveedor {proveedorId ?? "—"}
              {periodoSelRegistradas
                ? ` | Periodo ${fmtPeriodoMMYYYY(periodoSelRegistradas)}`
                : " | Todos los periodos"}
              )
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 180 }}>
              <Dropdown
                value={periodoSelRegistradas}
                options={periodoOptionsRegistradas}
                onChange={(e) => setPeriodoSelRegistradas(e.value)}
                placeholder="Periodo"
                style={{ width: "100%" }}
                disabled={isLoading || isLoadingRegistradas || !proveedorId}
              />
            </div>

            <Button
              label="Refrescar"
              icon="pi pi-refresh"
              className="p-button-text"
              onClick={cargarBoletasRegistradas}
              disabled={isLoading || isLoadingRegistradas || !proveedorId}
            />
          </div>
        </div>

        {isLoadingRegistradas ? (
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <ProgressSpinner />
          </div>
        ) : (
          <DataTable
            value={boletasRegistradasFiltradas}
            dataKey={(r) =>
              String(
                r?.id ?? `${r?.numeroBoleta ?? ""}-${r?.periodoHasta ?? ""}`,
              )
            }
            rowClassName={rowClassNameRegistradas}
            responsiveLayout="scroll"
            className="p-datatable-sm"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            stripedRows
            showGridlines
            emptyMessage={
              periodoSelRegistradas
                ? "No hay documentos registradss para este periodo."
                : "No hay documentos registrados para este proveedor."
            }
            style={{ marginTop: 12 }}
          >
            <Column
              field="numeroBoleta"
              header="N° Documento"
              style={{ minWidth: 140 }}
            />
            <Column
              field="cliente"
              header="Cliente"
              style={{ minWidth: 200 }}
            />
            <Column
              field="contrato"
              header="Contrato"
              style={{ minWidth: 140 }}
            />
            <Column field="ruta" header="Ruta" style={{ minWidth: 120 }} />
            <Column
              header="Fecha pago"
              body={(row) => fmtDDMMYYYY(row?.fechaPago)}
              style={{ minWidth: 140 }}
            />
            <Column
              header="Desde"
              body={(row) => fmtDDMMYYYY(row?.periodoDesde)}
              style={{ minWidth: 130 }}
            />
            <Column
              header="Hasta"
              body={(row) => fmtDDMMYYYY(row?.periodoHasta)}
              style={{ minWidth: 130 }}
            />
            <Column
              header="Monto"
              body={(row) => `$ ${fmtMoneyCLP(row?.montoTotal)}`}
              style={{ minWidth: 140 }}
            />
            <Column
              header="PDF"
              body={(row) => (
                <Button
                  label="Ver PDF"
                  icon="pi pi-file-pdf"
                  className="p-button-sm p-button-text"
                  onClick={() => openPdfUploadId(row?.pdfUploadId)}
                  disabled={!row?.pdfUploadId}
                />
              )}
              style={{ minWidth: 120 }}
            />
          </DataTable>
        )}
      </div>

      <Dialog
        header="Editar datos extraídos"
        visible={editOpen}
        onHide={() => setEditOpen(false)}
        style={{ width: "720px", maxWidth: "95vw" }}
        modal
      >
        <div className="grid">
          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Empresa (RUT)</label>
            <InputText
              value={editEmpresaRut}
              onChange={(e) => setEditEmpresaRut(e.target.value)}
              style={{ width: "100%" }}
              placeholder="Ej: 76.123.456-7"
              disabled={isLoading}
            />
            <small className="p-text-secondary"></small>
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Medidores</label>
            <InputText
              value={editMedidoresText}
              onChange={(e) => setEditMedidoresText(e.target.value)}
              style={{ width: "100%" }}
              placeholder="Ej: 12345, 67890, 11223"
              disabled={isLoading}
            />
            <small className="p-text-secondary">
              Separe por coma los medidores{" "}
            </small>
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>N° Boleta</label>
            <InputText
              value={getByPath(editData, "document.numero_boleta", "")}
              onChange={(e) =>
                updateField("document.numero_boleta", e.target.value)
              }
              style={{ width: "100%" }}
              placeholder="Ej: 29663684"
            />
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Fecha pago</label>
            <Calendar
              value={parseIsoToDate(
                getByPath(editData, "document.fecha_pago", ""),
              )}
              onChange={(e) =>
                updateField("document.fecha_pago", toIsoFromDate(e.value))
              }
              dateFormat="dd-mm-yy"
              showIcon
              maxDate={new Date()}
              placeholder="DD-MM-AAAA"
              style={{ width: "100%" }}
              inputStyle={{ width: "100%" }}
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Cliente</label>
            <InputText
              value={getByPath(editData, "header.cliente", "")}
              onChange={(e) => updateField("header.cliente", e.target.value)}
              style={{ width: "100%" }}
              placeholder="Nombre / RUT / Código cliente"
            />
          </div>

          <div className="col-12 md:col-3">
            <label style={{ fontWeight: "bold" }}>Contrato</label>
            <InputText
              value={getByPath(editData, "header.contrato", "")}
              onChange={(e) => updateField("header.contrato", e.target.value)}
              style={{ width: "100%" }}
              placeholder="Ej: 987654"
            />
          </div>

          <div className="col-12 md:col-3">
            <label style={{ fontWeight: "bold" }}>Ruta</label>
            <InputText
              value={getByPath(editData, "header.ruta", "")}
              onChange={(e) => updateField("header.ruta", e.target.value)}
              style={{ width: "100%" }}
              placeholder="Ej: 123-4"
            />
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Periodo desde</label>
            <Calendar
              value={parseIsoToDate(
                getByPath(editData, "header.periodo.desde", ""),
              )}
              onChange={(e) =>
                updateField("header.periodo.desde", toIsoFromDate(e.value))
              }
              dateFormat="dd-mm-yy"
              showIcon
              maxDate={new Date()}
              placeholder="DD-MM-AAAA"
              style={{ width: "100%" }}
              inputStyle={{ width: "100%" }}
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Periodo hasta</label>
            <Calendar
              value={parseIsoToDate(
                getByPath(editData, "header.periodo.hasta", ""),
              )}
              onChange={(e) =>
                updateField("header.periodo.hasta", toIsoFromDate(e.value))
              }
              dateFormat="dd-mm-yy"
              showIcon
              maxDate={new Date()}
              placeholder="DD-MM-AAAA"
              style={{ width: "100%" }}
              inputStyle={{ width: "100%" }}
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Monto total (CLP)</label>
            <InputNumber
              value={
                Number(getByPath(editData, "header.monto_total.valor", 0)) ||
                null
              }
              onValueChange={(e) =>
                updateField("header.monto_total.valor", e.value ?? null)
              }
              mode="currency"
              currency="CLP"
              locale="es-CL"
              maxFractionDigits={0}
              inputStyle={{ width: "100%" }}
              placeholder="$ 250.000"
            />
          </div>

          <div className="col-12 md:col-6">
            <label style={{ fontWeight: "bold" }}>Moneda</label>
            <Dropdown
              value={getByPath(editData, "header.monto_total.moneda", "CLP")}
              options={monedasOptions}
              onChange={(e) =>
                updateField("header.monto_total.moneda", e.value)
              }
              placeholder="Seleccione moneda"
              style={{ width: "100%" }}
              disabled={isLoading}
            />
          </div>

          <div
            className="col-12"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 10,
            }}
          >
            <Button
              label="Cancelar"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setEditOpen(false)}
            />
            <Button
              label="Guardar cambios"
              icon="pi pi-save"
              className="p-button-success"
              onClick={saveEdit}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={pdfTitle || "PDF"}
        visible={pdfOpen}
        onHide={() => setPdfOpen(false)}
        style={{ width: "980px", maxWidth: "98vw" }}
        modal
      >
        {!pdfUrl ? (
          <Message severity="warn" text="No hay URL de PDF para mostrar." />
        ) : (
          <iframe
            title="pdf-viewer"
            src={pdfUrl}
            style={{ width: "100%", height: "75vh", border: "none" }}
          />
        )}
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}
        >
          <Button
            label="Cerrar"
            icon="pi pi-times"
            className="p-button-text"
            onClick={() => setPdfOpen(false)}
          />
        </div>
      </Dialog>
      <style>{`
        .row-duplicada {
          background: #fff5f5 !important;
        }
      `}</style>
    </div>
  );
};

export default CargaBoletaPdf;
